import React from 'react';
import { Loader2, CheckCircle2, ScanLine, PaintBucket, Sofa } from 'lucide-react';
import { AppMode, ProcessingState } from '../types';

interface Props {
  processing: ProcessingState;
  mode: AppMode;
  elapsedSeconds: number;
}

const renderStep = (processing: ProcessingState, stepId: string, label: string, icon: React.ReactNode) => {
  const stepsOrder = ['uploading', 'renovating', 'staging', 'complete'];
  const currentIdx = stepsOrder.indexOf(processing.step);
  const stepIdx = stepsOrder.indexOf(stepId);
  const isActive = processing.step === stepId;
  const isCompleted = currentIdx > stepIdx;

  return (
    <div className={`flex items-center gap-3 p-3 border transition-all rounded-md ${isActive ? 'bg-white border-border shadow-card' : 'border-transparent opacity-50'}`}>
      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${isActive ? 'bg-accent text-ink animate-pulse' : isCompleted ? 'bg-black text-white' : 'bg-stone-200 text-stone-400'}`}>
        {isCompleted ? <CheckCircle2 size={16} /> : isActive ? <Loader2 size={16} className="animate-spin" /> : icon}
      </div>
      <div className="flex-1">
        <p className={`text-xs font-bold uppercase tracking-wide ${isActive ? 'text-ink' : 'text-ink/60'}`}>{label}</p>
        {isActive && <p className="text-[10px] text-ink/60 mt-1">Bezig...</p>}
      </div>
    </div>
  );
};

const FUN_FACTS = [
  'Ingerichte Funda-advertenties krijgen tot 3× meer views dan lege-huis-foto\'s.',
  'Niet tevreden? Klik op feedback en we genereren opnieuw.',
  'De Clean Base stap verwijdert bestaande meubels en herstelt muren.',
  'Gemiddeld 40% is meteen perfect. Plan 1-2 feedback-rondes voor de rest.',
  'Je feedback gaat letterlijk het volgende prompt in - het model leert per sessie.',
  'Elke render doorloopt twee AI-stappen: opruimen, dan inrichten.',
  'Je kunt meerdere varianten per kamer genereren en de beste kiezen.',
];

export const ProcessingSidebar: React.FC<Props> = ({ processing, mode, elapsedSeconds }) => {
  return (
    <div className="flex flex-col gap-3 md:gap-4">
      {renderStep(processing, 'uploading', 'Geometrie analyseren', <ScanLine size={16} />)}
      {mode === AppMode.FULL_RENOVATION && (
        renderStep(processing, 'renovating', 'Renovatie', <PaintBucket size={16} />)
      )}
      {renderStep(processing, 'staging', 'Inrichten', <Sofa size={16} />)}

      <div className="mt-4 md:mt-6 flex items-center justify-center">
        <div className="relative w-24 h-24">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#e7e5e4" strokeWidth="6" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#ccf381" strokeWidth="6"
              strokeDasharray={`${Math.min(elapsedSeconds / 75 * 264, 264)} 264`}
              strokeLinecap="round" className="transition-all duration-1000" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold">{elapsedSeconds}s</span>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 bg-white border border-border/10 rounded-md">
        <p className="text-[10px] text-ink/50 uppercase tracking-wide mb-2">Wist je dat?</p>
        <p className="text-xs text-ink/60 leading-relaxed">
          {FUN_FACTS[Math.floor(elapsedSeconds / 8) % FUN_FACTS.length]}
        </p>
      </div>
    </div>
  );
};
